import { useState, type FormEvent, type ReactNode } from 'react'
import { Link, Navigate, useLocation, useNavigate } from 'react-router-dom'

import { leerToken, mensajeDeError } from '../../lib/api'
import { useSession } from '../../lib/session'
import { Button, ErrorMessage, Field, Loading } from '../../ui'
import { CordMark } from '../../ui/Cord'

/* ===========================================================================
   Entrar.

   Un formulario de dos campos no necesita ilustración, columna partida ni
   testimonio: necesita verse corto. Una sola columna angosta, alineada a la
   izquierda, con la marca arriba haciendo también de camino de vuelta.
   =========================================================================== */

/**
 * RequireAuth manda aquí guardando en `state.desde` la ruta que el usuario
 * quería abrir. El estado del historial lo puede escribir cualquiera, así que
 * solo se acepta una ruta interna: nunca `//otro-sitio` ni una URL absoluta.
 */
function rutaDeVuelta(state: unknown): string | null {
  if (typeof state !== 'object' || state === null || !('desde' in state)) return null
  const { desde } = state as { desde?: unknown }
  if (typeof desde !== 'string') return null
  if (!desde.startsWith('/') || desde.startsWith('//')) return null
  return desde
}

function Marco({ children }: { children: ReactNode }) {
  return (
    <main className="flex min-h-dvh justify-center bg-bruma px-6 py-8 sm:py-12">
      <div className="w-full max-w-sm">
        <Link
          to="/"
          aria-label="Nuestra Boda — volver al inicio"
          className="inline-flex items-center gap-2.5 text-tinta"
        >
          <CordMark className="h-3.5 w-8 shrink-0 text-laton" />
          <span className="font-display text-base">Nuestra Boda</span>
        </Link>
        {children}
      </div>
    </main>
  )
}

export default function Entrar() {
  const { user, cargando, entrar } = useSession()
  const location = useLocation()
  const navigate = useNavigate()

  // Si hay token guardado, la sesión se está validando contra el servidor: se
  // espera en vez de enseñar un formulario que va a desaparecer solo.
  const [sesionPorRestaurar] = useState(() => leerToken() !== null)

  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [enviando, setEnviando] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const destino = rutaDeVuelta(location.state) ?? '/panel'

  async function alEnviar(evento: FormEvent<HTMLFormElement>) {
    evento.preventDefault()
    if (enviando) return

    setError(null)
    setEnviando(true)
    try {
      await entrar(email.trim(), password)
      navigate(destino, { replace: true })
    } catch (fallo) {
      // El servidor escribe el mensaje en español y para el usuario final:
      // se muestra tal cual, sin reinterpretarlo. Lo escrito no se borra.
      setError(mensajeDeError(fallo))
      setEnviando(false)
    }
  }

  if (user) return <Navigate to={destino} replace />
  if (cargando && sesionPorRestaurar) {
    return (
      <Marco>
        <Loading label="Comprobando tu sesión…" />
      </Marco>
    )
  }

  return (
    <Marco>
      <div className="mt-8 rounded-md border border-palo/60 bg-lienzo p-6">
        <div>
          <h1 className="font-display text-3xl text-tinta">Entra a tu cuenta</h1>
          <p className="mt-2 text-sm text-palo-hondo">
            Sigues la planificación donde la dejaste.
          </p>
        </div>

        <form onSubmit={alEnviar} className="mt-8 flex flex-col gap-4">
          <Field
            label="Correo"
            type="email"
            name="email"
            autoComplete="email"
            autoCapitalize="none"
            spellCheck={false}
            required
            value={email}
            onChange={(evento) => setEmail(evento.target.value)}
            disabled={enviando}
          />
          <Field
            label="Contraseña"
            type="password"
            name="password"
            autoComplete="current-password"
            required
            value={password}
            onChange={(evento) => setPassword(evento.target.value)}
            disabled={enviando}
          />

          {error && <ErrorMessage>{error}</ErrorMessage>}

          <Button type="submit" full loading={enviando}>
            Entrar
          </Button>
        </form>
      </div>

      <hr className="regla mt-8" />
      <p className="mt-3 text-sm text-palo-hondo">
        ¿Todavía no tienes cuenta?{' '}
        <Link
          to="/plataforma/registro"
          className="text-tinta underline decoration-laton underline-offset-4"
        >
          Crear una
        </Link>
      </p>
    </Marco>
  )
}
